import React from 'react';
import PropTypes from 'prop-types';
import tw, { styled } from 'twin.macro';

import { Button, Spinner, Flex } from '@ubisend/pulse-components';
import {
  motion,
  AnimatePresence,
  AnimateSharedLayout
} from '@ubisend/framer-motion';

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

// Styled Components
const SuggestionContainer = styled(motion.div).attrs({
  variants: containerVariants,
  initial: 'hidden',
  animate: 'visible'
})`
  ${tw`relative flex flex-col mb-4 pb-0 w-full`}
  width: calc(100vw - 512px);
  margin: 0 auto;
  padding-top: 1.8rem;

  @media (max-width: 800px) {
    display: none;
  }
`;

const HeaderRow = styled.div`
  ${tw`w-full flex justify-between`}
`;

const SuggestionTitle = styled.div`
  ${tw`absolute text-xs text-gray-500 text-left flex items-center`}
  top: 0;
  padding-top: 0.5rem;
  left: 0.5rem;
`;

const SuggestionNotice = styled.div`
  ${tw`absolute text-xs text-gray-500 text-right flex items-center`}
  height: 1.25rem;
  top: 0;
  padding-top: 0.5rem;
  right: 0.5rem;
`;

const SuggestionBox = styled(motion.div).attrs({
  variants: itemVariants,
  transition: { duration: 0.4, ease: 'easeOut' }
})`
  ${tw`flex items-center justify-between bg-white shadow p-2 w-full`}
  border-top: 1px solid #d1d5db;
`;

const SuggestionTextWrapper = styled.div`
  ${tw`flex-1 mr-4`}
  overflow: hidden;
`;

const SuggestionText = styled.span`
  ${tw`block text-sm`}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadingPlaceholder = ({ index }) => (
  <SuggestionBox key={`loading-${index}`}>
    <SuggestionTextWrapper>
      <Flex left>
        <SuggestionText tw="ml-2">
          <Spinner size="18px" /> Loading suggestion...
        </SuggestionText>
      </Flex>
    </SuggestionTextWrapper>
  </SuggestionBox>
);

LoadingPlaceholder.propTypes = {
  index: PropTypes.number.isRequired
};

const AgentComposerSuggestionList = ({ suggestions, setReply, isLoading, totalSlots }) => {
  return (
    <SuggestionContainer>
      <HeaderRow>
        <SuggestionTitle>Generated response</SuggestionTitle>
        <SuggestionNotice>Responses might be inaccurate</SuggestionNotice>
      </HeaderRow>

      <AnimateSharedLayout>
        <AnimatePresence>
          {Array.from({ length: totalSlots }).map((_, index) => {
            const suggestion = suggestions[index];

            if (isLoading && !suggestion) {
              return <LoadingPlaceholder key={`loading-${index}`} index={index} />;
            }

            if (suggestion) {
              return (
                <SuggestionBox key={`suggestion-${index}`} title={suggestion}>
                  <SuggestionTextWrapper>
                    <SuggestionText title={suggestion}>{suggestion}</SuggestionText>
                  </SuggestionTextWrapper>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="clipboardCopy"
                    onClick={() => setReply(suggestion)}
                  />
                </SuggestionBox>
              );
            }

            return null;
          })}
        </AnimatePresence>
      </AnimateSharedLayout>
    </SuggestionContainer>
  );
};

AgentComposerSuggestionList.propTypes = {
  suggestions: PropTypes.array.isRequired,
  setReply: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  totalSlots: PropTypes.number.isRequired,
};

export default AgentComposerSuggestionList;